import React, { useEffect, useMemo } from "react";
import {
  Box,
  useTheme,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
} from "@mui/material";
import Header from "components/Header";
import { useDispatch, useSelector } from "react-redux";
import MUIDataTable from "mui-datatables";
import { fetchListRekapTabungan } from "state/karyawanActions";
import SortableHeader from "components/SortableHeader";
import { PrintOutlined } from "@mui/icons-material";

const RekapTabunganBulanan = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { list_rekap_tabungan_karyawan } = useSelector(
    (state) => state.karyawan,
  );

  useEffect(() => {
    dispatch(fetchListRekapTabungan());
  }, [dispatch]);

  const [selectedYear, setSelectedYear] = React.useState("");
  const [rowsPerPage, setRowsPerPage] = React.useState(30);

  // Get unique years from data
  const availableYears = useMemo(() => {
    if (!list_rekap_tabungan_karyawan) return [];
    const years = [
      ...new Set(list_rekap_tabungan_karyawan.map((item) => item.tahun)),
    ];
    return years.sort((a, b) => b - a);
  }, [list_rekap_tabungan_karyawan]);

  // Set default year to most recent year
  useEffect(() => {
    if (availableYears.length > 0 && !selectedYear) {
      setSelectedYear(availableYears[0]);
    }
  }, [availableYears, selectedYear]);

  // Transform data for the selected year
  const transformedData = useMemo(() => {
    if (!list_rekap_tabungan_karyawan || !selectedYear) return [];

    // Filter data by selected year
    const yearData = list_rekap_tabungan_karyawan.filter(
      (item) => item.tahun === parseInt(selectedYear),
    );

    // Group by karyawan
    const groupedByKaryawan = {};
    yearData.forEach((item) => {
      const key = item.id_karyawan;
      if (!groupedByKaryawan[key]) {
        groupedByKaryawan[key] = {
          id_karyawan: item.id_karyawan,
          nama_karyawan: item.nama_karyawan,
        };
      }
      groupedByKaryawan[key][`bulan_${item.bulan_angka}`] = item.saldo_akhir;
    });

    // Calculate total saldo for each karyawan (use last month's value that has data)
    const result = Object.values(groupedByKaryawan).map((karyawan) => {
      // Find the last month that has a value
      let lastValue = 0;
      for (let i = 12; i >= 1; i--) {
        const bulanValue = karyawan[`bulan_${i}`];
        if (bulanValue && parseFloat(bulanValue) > 0) {
          lastValue = parseFloat(bulanValue);
          break;
        }
      }
      return {
        ...karyawan,
        total_saldo: lastValue.toFixed(2),
      };
    });

    return result;
  }, [list_rekap_tabungan_karyawan, selectedYear]);

  // Month names in order
  const monthNames = [
    { num: 1, name: "Januari" },
    { num: 2, name: "Februari" },
    { num: 3, name: "Maret" },
    { num: 4, name: "April" },
    { num: 5, name: "Mei" },
    { num: 6, name: "Juni" },
    { num: 7, name: "Juli" },
    { num: 8, name: "Agustus" },
    { num: 9, name: "September" },
    { num: 10, name: "Oktober" },
    { num: 11, name: "November" },
    { num: 12, name: "Desember" },
  ];

  // Build columns dynamically
  const columns = useMemo(() => {
    const baseColumns = [
      {
        name: "id_karyawan",
        label: "ID Karyawan",
        options: {
          filter: true,
          sort: true,
          customHeadRender: (columnMeta, handleToggleColumn, sortOrder) => (
            <SortableHeader
              key={`col-${columnMeta.index}`}
              label={columnMeta.label}
              columnName={columnMeta.name}
              sortOrder={sortOrder}
              onClick={() => handleToggleColumn(columnMeta.index)}
              sorting
            />
          ),
        },
      },
      {
        name: "nama_karyawan",
        label: "Nama Karyawan",
        options: {
          filter: true,
          sort: true,
          customHeadRender: (columnMeta, handleToggleColumn, sortOrder) => (
            <SortableHeader
              key={`col-${columnMeta.index}`}
              label={columnMeta.label}
              columnName={columnMeta.name}
              sortOrder={sortOrder}
              onClick={() => handleToggleColumn(columnMeta.index)}
              sorting
            />
          ),
        },
      },
    ];

    // Add month columns
    monthNames.forEach((month) => {
      baseColumns.push({
        name: `bulan_${month.num}`,
        label: month.name,
        options: {
          filter: false,
          sort: true,
          customHeadRender: (columnMeta, handleToggleColumn, sortOrder) => (
            <SortableHeader
              key={`col-${columnMeta.index}`}
              label={columnMeta.label}
              columnName={columnMeta.name}
              sortOrder={sortOrder}
              onClick={() => handleToggleColumn(columnMeta.index)}
              sorting
            />
          ),
          customBodyRender: (value) => {
            if (!value || value === "0.00" || value === "0") return "-";
            return new Intl.NumberFormat("id-ID", {
              style: "currency",
              currency: "IDR",
              minimumFractionDigits: 0,
            }).format(parseFloat(value));
          },
        },
      });
    });

    // Add Total Saldo column
    baseColumns.push({
      name: "total_saldo",
      label: "Total Saldo",
      options: {
        filter: false,
        sort: true,
        customHeadRender: (columnMeta, handleToggleColumn, sortOrder) => (
          <SortableHeader
            key={`col-${columnMeta.index}`}
            label={columnMeta.label}
            columnName={columnMeta.name}
            sortOrder={sortOrder}
            onClick={() => handleToggleColumn(columnMeta.index)}
            sorting
          />
        ),
        customBodyRender: (value) => {
          if (!value || value === "0.00" || value === "0") return "-";
          return new Intl.NumberFormat("id-ID", {
            style: "currency",
            currency: "IDR",
            minimumFractionDigits: 0,
          }).format(parseFloat(value));
        },
      },
    });

    return baseColumns;
  }, []);

  const openCetakRekapTabungan = () => {
    const printWindow = window.open("/cetak-rekap-tabungan", "_blank");
    if (printWindow) printWindow.focus();
  };

  const handlePrintPDF = () => {
    sessionStorage.setItem(
      "datacetakrekaptabungan",
      JSON.stringify({
        data: transformedData,
        year: selectedYear,
      }),
    );
    openCetakRekapTabungan();
  };

  const options = {
    selectableRows: "none",
    filterType: "dropdown",
    filter: false,
    fixedHeader: false,
    download: false,
    print: false,
    fixedSelectColumn: false,
    search: true,
    viewColumns: false,
    responsive: "standard",
    customToolbar: () => {
      return (
        <>
          <IconButton onClick={handlePrintPDF}>
            <PrintOutlined />
          </IconButton>
          <FormControl size="small" sx={{ minWidth: 150, mr: 1 }}>
            <InputLabel id="year-select-label">Tahun</InputLabel>
            <Select
              labelId="year-select-label"
              value={selectedYear}
              label="Tahun"
              onChange={(e) => setSelectedYear(e.target.value)}
              sx={{
                color: theme.palette.secondary[100],
                ".MuiOutlinedInput-notchedOutline": {
                  borderColor: theme.palette.secondary[100],
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: theme.palette.secondary[100],
                },
                ".MuiSvgIcon-root": {
                  color: theme.palette.secondary[100],
                },
              }}
            >
              {availableYears.map((year) => (
                <MenuItem key={year} value={year}>
                  {year}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </>
      );
    },
    rowsPerPageOptions: [30, 60, transformedData.length],
    rowsPerPage: rowsPerPage,
    onChangeRowsPerPage: (numberOfRows) => setRowsPerPage(numberOfRows),
  };

  return (
    <Box m="1.5rem 2.5rem">
      <Header
        title="Rekap Tabungan Bulanan"
        subtitle={`Daftar rekap tabungan bulanan karyawan tahun ${selectedYear || "-"}`.replace(
          "tahun -",
          "tahun",
        )}
      />
      <Box
        mt="40px"
        height="75vh"
        sx={{
          "& .MuiDataGrid-root": {
            border: "none",
          },
          "& .MuiDataGrid-cell": {
            borderBottom: "none",
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: theme.palette.background.alt,
            color: theme.palette.secondary[100],
            borderBottom: "none",
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor: theme.palette.primary.light,
          },
          "& .MuiDataGrid-footerContainer": {
            backgroundColor: theme.palette.background.alt,
            color: theme.palette.secondary[100],
            borderTop: "none",
          },
          "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
            color: `${theme.palette.secondary[200]} !important`,
          },
        }}
      >
        <MUIDataTable
          title={""}
          data={transformedData}
          columns={columns}
          options={options}
        />
      </Box>
    </Box>
  );
};

export default RekapTabunganBulanan;
